/** G4（信号与证据）五屏的契约解析与取数：收件索引、拒收汇总、快照复算、镜像新鲜度、库核对、库查询、变更台账。
 *
 * 形状以后端为准（主人 2026-10-08 裁决）：字段名照 `someo_keel/api/console_g4.py` 与 `api/store.py` 原样，
 * 缺键抛 `ContractError`，**不补默认值**——补 0 / 补空数组会把「后端没按契约回答」伪装成「没有数据」。
 * 值可以是 null 的字段（如 `lag_days`、`notified`）保留 null，由屏决定标「缺」还是「—」。
 */

import { useQuery } from "@tanstack/react-query";

import { apiUrl, expectList, expectRecord, query, requireBoolean, requireNumber, requireString } from "./client";
import { ApiError, ContractError, isRetryable } from "./errors";
import type { ReadResult } from "./queries";
import type { BridgeReport, DailySnapshot } from "./types";
import { readBridgeReport, readDailyDetail } from "./contract";

// ---- 小工具 -----------------------------------------------------------------

function has(r: Record<string, unknown>, k: string, url: string): unknown {
  if (!(k in r)) throw new ContractError(`缺字段 \`${k}\``, url, r);
  return r[k];
}
function nStr(r: Record<string, unknown>, k: string, url: string): string | null {
  const v = has(r, k, url);
  if (v !== null && typeof v !== "string") throw new ContractError(`字段 \`${k}\` 应为字符串或 null`, url, r);
  return v as string | null;
}
function nNum(r: Record<string, unknown>, k: string, url: string): number | null {
  const v = has(r, k, url);
  if (v !== null && typeof v !== "number") throw new ContractError(`字段 \`${k}\` 应为数字或 null`, url, r);
  return v as number | null;
}
function nBool(r: Record<string, unknown>, k: string, url: string): boolean | null {
  const v = has(r, k, url);
  if (v !== null && typeof v !== "boolean") throw new ContractError(`字段 \`${k}\` 应为布尔或 null`, url, r);
  return v as boolean | null;
}
function strList(r: Record<string, unknown>, k: string, url: string): string[] {
  const v = has(r, k, url);
  if (!Array.isArray(v) || v.some((x) => typeof x !== "string")) throw new ContractError(`字段 \`${k}\` 应为字符串数组`, url, r);
  return v as string[];
}
function nStrList(r: Record<string, unknown>, k: string, url: string): string[] | null {
  return has(r, k, url) === null ? null : strList(r, k, url);
}

/** 501「接口未提供」：屏按规格显示路径与 detail，不当成契约错误，也不当成空结果。 */
export function notProvided(error: unknown): { detail: string } | null {
  if (error instanceof ApiError && error.kind === "http" && error.status === 501) {
    return { detail: error.message.replace(/^HTTP 501：?/, "") };
  }
  return null;
}

// ---- 收件索引 -----------------------------------------------------------------

export interface InboxIndexRow {
  strategy_id: string;
  source_line: string | null;
  date: string;
  shape: string;
  accepted: number;
  rejected: number;
  no_order: number;
  input_count: number | null;
}

export function parseInboxIndex(body: unknown): InboxIndexRow[] {
  const url = apiUrl("/signals/inbox");
  if (!Array.isArray(body)) throw new ContractError("收件索引应为数组", url, body);
  return body.map((item, i) => {
    const r = expectRecord(item, url, `收件索引[${i}]`);
    return {
      strategy_id: requireString(r, "strategy_id", url),
      source_line: nStr(r, "source_line", url),
      date: requireString(r, "date", url),
      shape: requireString(r, "shape", url),
      accepted: requireNumber(r, "accepted", url),
      rejected: requireNumber(r, "rejected", url),
      no_order: requireNumber(r, "no_order", url),
      input_count: nNum(r, "input_count", url),
    };
  });
}

/** 收件动作：`GET /paper/events` 里 `kind=bridge`（桥接）与 `kind=inbox_ingest`（手工落收件，#5010）的行；其它 kind 由停机屏解析。 */
export interface BridgeEvent {
  kind: "bridge" | "inbox_ingest";
  ts_ns: number;
  operator: string;
  reason: string;
  source: string;
  /** 以下四项是 #4607 补的字段；旧事件没有，原样 null（屏上标「缺」）。 */
  trade_date: string | null;
  strategies: string[] | null;
  input_count: number | null;
  outcome: string | null;
}

export function parseBridgeEvents(body: unknown): BridgeEvent[] {
  const url = apiUrl("/paper/events");
  if (!Array.isArray(body)) throw new ContractError("事件应为数组", url, body);
  return body
    .map((item, i) => expectRecord(item, url, `事件[${i}]`))
    .filter((r) => r["kind"] === "bridge" || r["kind"] === "inbox_ingest")
    .map((r) => ({
      kind: r["kind"] as BridgeEvent["kind"],
      ts_ns: requireNumber(r, "ts_ns", url),
      operator: requireString(r, "operator", url),
      reason: requireString(r, "reason", url),
      source: requireString(r, "source", url),
      trade_date: typeof r["trade_date"] === "string" ? r["trade_date"] : null,
      strategies: Array.isArray(r["strategies"]) ? (r["strategies"] as unknown[]).map(String) : null,
      input_count: typeof r["input_count"] === "number" ? r["input_count"] : null,
      outcome: typeof r["outcome"] === "string" ? r["outcome"] : null,
    }));
}

// ---- 拒收汇总 -----------------------------------------------------------------

export interface Rejection {
  stage: "bridge" | "run" | "deliver";
  source_line: string | null;
  strategy_id: string | null;
  trade_date: string | null;
  ref: string | null;
  reason: string | null;
  evidence: string;
  notified: boolean | null;
  notified_utc: string | null;
  notified_via: string | null;
}
export interface RejectionsPayload {
  rejections: Rejection[];
  counts: { bridge: number; run: number; deliver: number };
}

export function parseRejections(body: unknown): RejectionsPayload {
  const url = apiUrl("/signals/rejections");
  const r = expectRecord(body, url, "拒收汇总");
  const counts = expectRecord(r["counts"], url, "counts");
  return {
    rejections: expectList(r, "rejections", url, "拒收汇总").map((x) => {
      const stage = requireString(x, "stage", url);
      if (stage !== "bridge" && stage !== "run" && stage !== "deliver") throw new ContractError(`未知环节 ${stage}`, url, x);
      return {
        stage,
        source_line: nStr(x, "source_line", url),
        strategy_id: nStr(x, "strategy_id", url),
        trade_date: nStr(x, "trade_date", url),
        ref: nStr(x, "ref", url),
        reason: nStr(x, "reason", url),
        evidence: requireString(x, "evidence", url),
        notified: nBool(x, "notified", url),
        notified_utc: nStr(x, "notified_utc", url),
        notified_via: nStr(x, "notified_via", url),
      };
    }),
    counts: {
      bridge: requireNumber(counts, "bridge", url),
      run: requireNumber(counts, "run", url),
      deliver: requireNumber(counts, "deliver", url),
    },
  };
}

// ---- 快照复算 -----------------------------------------------------------------

export interface SnapshotVerify {
  snapshot_id: string;
  recorded_sha256: string;
  recomputed_sha256: string | null;
  verifiable: boolean;
  match: boolean | null;
  files: { path: string; recorded: string; recomputed: string | null; match: boolean; exists: boolean }[];
  detail: string | null;
  checked_utc: string;
}

export function parseSnapshotVerify(body: unknown, snapshotId: string): SnapshotVerify {
  const url = apiUrl(`/snapshots/${encodeURIComponent(snapshotId)}/verify`);
  const r = expectRecord(body, url, "快照复算");
  return {
    snapshot_id: requireString(r, "snapshot_id", url),
    recorded_sha256: requireString(r, "recorded_sha256", url),
    recomputed_sha256: nStr(r, "recomputed_sha256", url),
    verifiable: requireBoolean(r, "verifiable", url),
    match: nBool(r, "match", url),
    files: expectList(r, "files", url, "复算文件").map((f) => ({
      path: requireString(f, "path", url),
      recorded: requireString(f, "recorded", url),
      recomputed: nStr(f, "recomputed", url),
      match: requireBoolean(f, "match", url),
      exists: requireBoolean(f, "exists", url),
    })),
    detail: nStr(r, "detail", url),
    checked_utc: requireString(r, "checked_utc", url),
  };
}

// ---- 镜像新鲜度 ---------------------------------------------------------------

export interface Freshness {
  latest_day: string | null;
  lag_days: number | null;
  missing_days: string[] | null;
  stale: boolean | null;
  cadence_days: number | null;
}
export interface MirrorCollection extends Freshness {
  name: string;
  source_line: string | null;
  count: number | null;
  latest: unknown;
  incoming: number | null;
  deleted: number | null;
  refused: number | null;
}
export interface LineFreshness extends Freshness {
  source_line: string;
  basis: string;
}
export interface MirrorPayload {
  present: boolean;
  record_path: string;
  checked_utc: string | null;
  run_started_utc: string | null;
  lookback_days: number | null;
  failed: string[];
  reference_day: string | null;
  collections: MirrorCollection[];
  lines: LineFreshness[];
  cadence_days: Record<string, number>;
}

function parseFreshness(r: Record<string, unknown>, url: string): Freshness {
  return {
    latest_day: nStr(r, "latest_day", url),
    lag_days: nNum(r, "lag_days", url),
    missing_days: nStrList(r, "missing_days", url),
    stale: nBool(r, "stale", url),
    cadence_days: nNum(r, "cadence_days", url),
  };
}

export function parseMirror(body: unknown): MirrorPayload {
  const url = apiUrl("/data/mirror");
  const r = expectRecord(body, url, "镜像新鲜度");
  const cadence = expectRecord(r["cadence_days"], url, "cadence_days");
  return {
    present: requireBoolean(r, "present", url),
    record_path: requireString(r, "record_path", url),
    checked_utc: nStr(r, "checked_utc", url),
    run_started_utc: nStr(r, "run_started_utc", url),
    lookback_days: nNum(r, "lookback_days", url),
    failed: strList(r, "failed", url),
    reference_day: nStr(r, "reference_day", url),
    collections: expectList(r, "collections", url, "镜像集合").map((c) => ({
      name: requireString(c, "name", url),
      source_line: nStr(c, "source_line", url),
      count: nNum(c, "count", url),
      latest: has(c, "latest", url),
      incoming: nNum(c, "incoming", url),
      deleted: nNum(c, "deleted", url),
      refused: nNum(c, "refused", url),
      ...parseFreshness(c, url),
    })),
    lines: expectList(r, "lines", url, "来源线").map((l) => ({
      source_line: requireString(l, "source_line", url),
      basis: requireString(l, "basis", url),
      ...parseFreshness(l, url),
    })),
    cadence_days: Object.fromEntries(Object.keys(cadence).map((k) => [k, requireNumber(cadence, k, url)])),
  };
}

// ---- 库文件核对 / 库内盘点 / 库查询 -------------------------------------------

export interface StoreVerify {
  present: boolean;
  record_path: string;
  ok: boolean | null;
  checks: { name: string; ok: boolean; detail: string[] }[];
  checked_utc: string | null;
  var: string | null;
  mirror: boolean | null;
}

export function parseStoreVerify(body: unknown): StoreVerify {
  const url = apiUrl("/store/verify");
  const r = expectRecord(body, url, "核对结果");
  return {
    present: requireBoolean(r, "present", url),
    record_path: requireString(r, "record_path", url),
    ok: nBool(r, "ok", url),
    checks: expectList(r, "checks", url, "核对项").map((c) => ({
      name: requireString(c, "name", url),
      ok: requireBoolean(c, "ok", url),
      detail: strList(c, "detail", url),
    })),
    checked_utc: nStr(r, "checked_utc", url),
    var: nStr(r, "var", url),
    mirror: nBool(r, "mirror", url),
  };
}

/** 库表版本与「模型与迁移是否一致」（`GET /store/status` 的 `schema`，#5012）。 */
export interface StoreSchema {
  /** 库表版本 = 库里最后一条已执行的迁移；库还没迁移过为 null。 */
  version: string | null;
  /** 仓里最新的迁移文件。 */
  latest: string | null;
  applied: string[];
  unapplied: string[];
  up_to_date: boolean;
  /** 模型与迁移文件是否一致；判不了为 null（不当成一致）。 */
  models_match_migrations: boolean | null;
  model_changes: string[];
  model_check_error: string | null;
}

export interface StoreStatus {
  reused: { table: string; rows: number; from: string | null; to: string | null }[];
  own: { table: string; rows: number }[];
  /** 旧版后端没有这个字段时为 null（滚动上线期间）。 */
  schema: StoreSchema | null;
}

export function parseStoreSchema(value: unknown, url: string): StoreSchema {
  const r = expectRecord(value, url, "库表版本");
  return {
    version: nStr(r, "version", url),
    latest: nStr(r, "latest", url),
    applied: strList(r, "applied", url),
    unapplied: strList(r, "unapplied", url),
    up_to_date: requireBoolean(r, "up_to_date", url),
    models_match_migrations: nBool(r, "models_match_migrations", url),
    model_changes: strList(r, "model_changes", url),
    model_check_error: "model_check_error" in r ? nStr(r, "model_check_error", url) : null,
  };
}

export function parseStoreStatus(body: unknown): StoreStatus {
  const url = apiUrl("/store/status");
  const r = expectRecord(body, url, "库内盘点");
  return {
    reused: expectList(r, "reused", url, "沿用表").map((t) => ({
      table: requireString(t, "table", url),
      rows: requireNumber(t, "rows", url),
      from: t["from"] === null || t["from"] === undefined ? null : String(t["from"]),
      to: t["to"] === null || t["to"] === undefined ? null : String(t["to"]),
    })),
    own: expectList(r, "own", url, "自有表").map((t) => ({ table: requireString(t, "table", url), rows: requireNumber(t, "rows", url) })),
    schema: r["schema"] === undefined ? null : parseStoreSchema(r["schema"], url),
  };
}

export interface MarketBars {
  symbol: string;
  bars: Record<string, unknown>[];
}

export function parseMarketBars(body: unknown): MarketBars {
  const url = apiUrl("/market/bars");
  const r = expectRecord(body, url, "行情查询");
  return { symbol: requireString(r, "symbol", url), bars: expectList(r, "bars", url, "行情") };
}

export interface Books {
  accounts: Record<string, unknown>[];
  someopark_users: { user_id: string; days: number; first: string | null; last: string | null }[];
}

export function parseBooks(body: unknown): Books {
  const url = apiUrl("/books");
  const r = expectRecord(body, url, "账本清单");
  return {
    accounts: expectList(r, "accounts", url, "内核账户"),
    someopark_users: expectList(r, "someopark_users", url, "投研侧用户").map((u) => ({
      user_id: requireString(u, "user_id", url),
      days: requireNumber(u, "days", url),
      first: u["first"] == null ? null : String(u["first"]),
      last: u["last"] == null ? null : String(u["last"]),
    })),
  };
}

// ---- 变更台账 -----------------------------------------------------------------

export interface ChangeRecord {
  change_id: string;
  ts_utc: string;
  kind: string;
  subject: string;
  before: string | null;
  after: string | null;
  operator: string;
  review: { verdict: string; review_id: string | null; summary: string | null };
  env_note: string | null;
}

/** `GET /changes` 字段草案（changelog.md）；现后端如实 501，这个解析器只在接上数据源后才会走到。 */
export function parseChanges(body: unknown): ChangeRecord[] {
  const url = apiUrl("/changes");
  return expectList(body, "changes", url, "变更").map((c) => {
    const review = expectRecord(c["review"], url, "review");
    return {
      change_id: requireString(c, "change_id", url),
      ts_utc: requireString(c, "ts_utc", url),
      kind: requireString(c, "kind", url),
      subject: requireString(c, "subject", url),
      before: nStr(c, "before", url),
      after: nStr(c, "after", url),
      operator: requireString(c, "operator", url),
      review: { verdict: requireString(review, "verdict", url), review_id: nStr(review, "review_id", url), summary: nStr(review, "summary", url) },
      env_note: nStr(c, "env_note", url),
    };
  });
}

// ---- 取数 -------------------------------------------------------------------

export const readInboxIndex = () => query<unknown>("/signals/inbox").then(parseInboxIndex);
export const readBridgeEvents = () => query<unknown>("/paper/events", { limit: 200 }).then(parseBridgeEvents);
export const readRejections = (params: { from?: string; to?: string; line?: string } = {}) =>
  query<unknown>("/signals/rejections", params).then(parseRejections);
export const readSnapshotVerify = (id: string) =>
  query<unknown>(`/snapshots/${encodeURIComponent(id)}/verify`).then((b) => parseSnapshotVerify(b, id));
export const readMirror = () => query<unknown>("/data/mirror").then(parseMirror);
export const readStoreVerify = () => query<unknown>("/store/verify").then(parseStoreVerify);
export const readStoreStatus = () => query<unknown>("/store/status").then(parseStoreStatus);
export const readBooks = () => query<unknown>("/books").then(parseBooks);
export const readMarketBars = (symbol: string, limit: number) => query<unknown>("/market/bars", { symbol, limit }).then(parseMarketBars);
export const readChanges = () => query<unknown>("/changes").then(parseChanges);

const READ = {
  // 501 是确定答案：不重试（isRetryable 对 4xx/501 之外的 5xx 才重试，这里再显式排掉 501）。
  retry: (count: number, error: unknown) => count < 2 && isRetryable(error) && notProvided(error) === null,
  retryDelay: 1_500,
} as const;

function use<T>(key: readonly unknown[], fn: () => Promise<T>, enabled = true): ReadResult<T> {
  return useQuery<T, Error>({ queryKey: key, queryFn: fn, enabled, ...READ });
}

export const useInboxIndex = () => use(["g4", "inbox-index"], readInboxIndex);
export const useInboxDetail = (strategy: string, date: string): ReadResult<BridgeReport> =>
  use(["g4", "inbox", strategy, date], () => readBridgeReport(date, strategy), Boolean(strategy && date));
export const useBridgeEvents = () => use(["g4", "bridge-events"], readBridgeEvents);
export const useRejections = (params: { from?: string; to?: string; line?: string }) =>
  use(["g4", "rejections", params], () => readRejections(params));
export const useSnapshotVerify = (id: string | null) =>
  use(["g4", "snapshot-verify", id], () => readSnapshotVerify(id as string), Boolean(id));
export const useMirror = () => use(["g4", "mirror"], readMirror);
export const useStoreVerify = () => use(["g4", "store-verify"], readStoreVerify);
export const useStoreStatus = () => use(["g4", "store-status"], readStoreStatus);
export const useBooks = () => use(["g4", "books"], readBooks);
export const useMarketBars = (symbol: string, limit: number) =>
  use(["g4", "market-bars", symbol, limit], () => readMarketBars(symbol, limit), Boolean(symbol));
export const useChanges = () => use(["g4", "changes"], readChanges);
export const useDailyDetail = (date: string | null): ReadResult<DailySnapshot> =>
  use(["g4", "daily", date], () => readDailyDetail(date as string), Boolean(date));

// ---- 纯逻辑（屏用，单测覆盖）---------------------------------------------------

/** 按线按日结论：后端 `/chain/daily` 未提供时只能标「缺」，不拿「有无文件」推断结论（inbox.md 错误态）。 */
export function lineOfStrategy(strategyId: string): string | null {
  if (strategyId.startsWith("someopark-test.")) return "someopark-test";
  if (strategyId.startsWith("someopark.")) return "someopark";
  return null;
}

/** 新鲜度一句话：`stale=null` 不判（事件型集合 / 没有日期），不写成「正常」。 */
export function freshnessText(f: Freshness): { tone: "ok" | "bad" | "neutral"; text: string } {
  if (f.stale === null) {
    return { tone: "neutral", text: f.cadence_days === null ? "不判新鲜度（事件型）" : "缺：无最新日期或参照日" };
  }
  if (f.stale) {
    const names = f.missing_days && f.missing_days.length ? `，漏 ${f.missing_days.join("、")}` : "";
    return { tone: "bad", text: `落后 ${f.lag_days} 个交易日（约定 ${f.cadence_days} 天）${names}` };
  }
  return { tone: "ok", text: `节奏正常，最近 ${f.latest_day}` };
}

/** 收件结论口径：上游无记录 / 有记录但都不下单 / 无拒收——三种空分三句话说。 */
export function inboxEmptyNotes(detail: BridgeReport): string[] {
  const notes: string[] = [];
  if (detail.signals.length === 0 && detail.no_order.length > 0) notes.push("上游有记录，但都不产生订单");
  if (detail.rejected.length === 0) notes.push("无拒收");
  if (detail.delivery === null) notes.push("尚未被纸上盘消费");
  return notes;
}
